/**
 * Attendance Card Component
 *
 * A card-based display for attendance records, designed for mobile-friendly staff/coach viewing.
 * Shows student name, class info, date/time, status, instructor notes, and makeup indicator.
 */

import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FiAlertTriangle, FiBook, FiCalendar, FiClock } from "react-icons/fi"

import { AttendanceActionsMenu } from "@/components/layout/Common/AttendanceActionsMenu"
import type { AttendanceResponse } from "@/types/attendance"

const statusColors: Record<string, string> = {
  present: "green",
  absent: "red",
  excused: "blue",
  late: "orange",
  makeup: "purple",
  cancelled: "gray",
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "N/A"
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString()
}

export interface AttendanceCardProps {
  attendance: AttendanceResponse
  /** Whether to show action menu */
  showActions?: boolean
  /** Click handler for the card */
  onClick?: () => void
}

export function AttendanceCard({
  attendance,
  showActions = true,
  onClick,
}: AttendanceCardProps) {
  const status = attendance.status || "Not marked"
  const statusColor = statusColors[status] || "gray"
  const studentName = (attendance as any).student_name || attendance.student_id
  const className = (attendance as any).class_name || null

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "ui.main",
        transform: onClick ? "translateY(-2px)" : undefined,
      }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header with student name and status */}
          <Flex justify="space-between" align="start">
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                {studentName}
              </Text>
              <Badge
                colorPalette={statusColor}
                variant="subtle"
                size="sm"
                borderRadius="full"
                mt={1}
              >
                {status}
              </Badge>
            </Box>

            {/* Actions menu */}
            {showActions && (
              <Box onClick={(e) => e.stopPropagation()}>
                <AttendanceActionsMenu attendance={attendance} />
              </Box>
            )}
          </Flex>

          {/* Class info + Date/Time */}
          <VStack align="stretch" gap={2}>
            {className && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiBook />
                </Icon>
                <Text>{className}</Text>
              </HStack>
            )}

            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiCalendar />
              </Icon>
              <Text>{formatDate(attendance.class_date)}</Text>
            </HStack>

            {attendance.class_time && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiClock />
                </Icon>
                <Text>{attendance.class_time}</Text>
              </HStack>
            )}
          </VStack>

          {/* Instructor notes preview */}
          {attendance.instructor_notes && (
            <Text fontSize="sm" color="gray.500" lineClamp={2}>
              {attendance.instructor_notes}
            </Text>
          )}

          {/* Makeup indicator */}
          {attendance.is_makeup_class && (
            <Box
              bg="purple.50"
              borderRadius="md"
              p={2}
              borderLeftWidth="3px"
              borderLeftColor="purple.400"
            >
              <HStack color="purple.600" fontSize="sm">
                <Icon>
                  <FiAlertTriangle />
                </Icon>
                <Text fontWeight="medium">Makeup Class</Text>
                {attendance.makeup_for_date && (
                  <Text color="gray.600">
                    (for {formatDate(attendance.makeup_for_date)})
                  </Text>
                )}
              </HStack>
            </Box>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default AttendanceCard
